import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, ShieldCheck } from "lucide-react";
import { useNav, VIEWS } from "@/lib/nav";
import { sectionBadgeColor, sectionLabel, SpellStats } from "@/components/RuleComponents";
import { ModelProfile } from "@/components/ModelProfile";

export function CardDetail({ cardId }) {
  const { navigate } = useNav();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getCard(cardId).then((c) => {
      setCard(c);
      setLoading(false);
    });
  }, [cardId]);

  if (loading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading card…
      </div>
    );
  if (!card) return <p>Card not found.</p>;

  // Group rules by section
  const groups = { cardAbility: [], modelAbility: [], weaponAbility: [], feat: [], spell: [] };
  for (const r of card.rules) {
    (groups[r.section] || []).push(r);
  }

  return (
    <div className="grid gap-4" data-testid="card-detail">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-md bg-gradient-to-br from-cyan-500/30 to-indigo-500/30 border border-cyan-700/40 flex items-center justify-center">
              <Users className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold leading-tight">{card.name}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {card.faction && (
                  <Badge variant="outline" className="text-amber-300 border-amber-700/40">
                    {card.faction}
                  </Badge>
                )}
                {card.type && (
                  <Badge variant="outline" className="text-purple-300 border-purple-700/40">
                    {card.type}
                  </Badge>
                )}
                {card.point_cost && card.point_cost !== "0" && (
                  <Badge variant="outline" data-testid="card-point-cost">{card.point_cost} pts</Badge>
                )}
                {card.field_allowance && <Badge variant="outline" data-testid="card-fa">FA {card.field_allowance}</Badge>}
                {card.health?.value && (
                  <Badge variant="outline" className="text-rose-300 border-rose-800/50" data-testid="card-health">
                    {card.health.value} {card.health.type === "Spiral" ? "spiral" : card.health.type === "Grid" ? "grid" : "boxes"}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {card.rules.length} special rule{card.rules.length === 1 ? "" : "s"} on this card
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {card.models?.length > 0 && (
        <Card data-testid="card-models">
          <CardContent className="p-4 space-y-5">
            {card.models.map((m, i) => (
              <ModelProfile key={m.id} model={m} index={i} showName={card.models.length > 1} />
            ))}
          </CardContent>
        </Card>
      )}

      {card.rules.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            This card has no indexed special rules.
          </CardContent>
        </Card>
      )}

      {[
        { key: "cardAbility", label: "Card abilities (apply to the whole card / unit)" },
        { key: "modelAbility", label: "Model abilities" },
        { key: "weaponAbility", label: "Weapon abilities (from this card\u2019s weapons)" },
        { key: "feat", label: "Feat" },
        { key: "spell", label: card.type === "Warbeast" ? "Animus" : "Spells" },
      ].map((sec) =>
        groups[sec.key].length > 0 ? (
          <Card key={sec.key} data-testid={`card-section-${sec.key}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm">{sec.label}</h3>
                <Badge variant="outline" className="ml-1">{groups[sec.key].length}</Badge>
              </div>
              <div className="space-y-2">
                {groups[sec.key].map((r) => (
                  <button
                    key={r.id}
                    onClick={() => navigate(VIEWS.rule(r.id))}
                    className="w-full text-left p-3 rounded border border-border bg-background/40 hover:border-primary/60 hover:bg-card/70 transition"
                    data-testid={`card-rule-${r.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium">{r.name}</span>
                      <Badge variant="outline" className={sectionBadgeColor(r.section)}>
                        {sectionLabel(r.section)}
                      </Badge>
                    </div>
                    {r.stats && <div className="mb-1.5"><SpellStats stats={r.stats} testId={`card-spell-stats-${r.id}`} /></div>}
                    <p className={`text-sm text-muted-foreground leading-snug ${r.section === "feat" ? "" : "line-clamp-2"}`}>
                      {r.text}
                    </p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null
      )}
    </div>
  );
}
